import { IWebsiteBuilderFormItemRaw } from "redux/reducers/models/websiteBuilder";
import { IFormBuilderEntityBase } from "../formBuilderEntity";
import {
  formBuilderGenerateDefaultResetButton,
  formBuilderGenerateDefaultSubmitButton,
  IFormBuilderButton,
  isFormBuilderButtonRole,
  isFormBuilderButtonType,
  TFormBuilderButtonRole,
} from "./formBuilderButton";
import {
  formBuilderGenerateDefaultCustomCheckbox,
  IFormBuilderCheckbox,
  isFormBuilderCheckboxRole,
  isFormBuilderCheckboxType,
  TFormBuilderCheckboxRole,
} from "./formBuilderCheckbox";
import {
  formBuilderGenerateDefaultCustomDate,
  IFormBuilderDate,
  isFormBuilderDateRole,
  isFormBuilderDateType,
  TFormBuilderDateRole,
} from "./formBuilderDate";
import {
  formBuilderGenerateDefaultAddressInput,
  formBuilderGenerateDefaultCustomInput,
  formBuilderGenerateDefaultEmailInput,
  formBuilderGenerateDefaultFirstNameInput,
  formBuilderGenerateDefaultLastNameInput,
  formBuilderGenerateDefaultPhoneInput,
  IFormBuilderInput,
  isFormBuilderInputRole,
  isFormBuilderInputType,
  TFormBuilderInputRole,
} from "./formBuilderInput";
import {
  formBuilderGenerateDefaultCustomSelect,
  formBuilderGenerateDefaultInquirySelect,
  IFormBuilderSelect,
  isFormBuilderSelectRole,
  isFormBuilderSelectType,
  TFormBuilderSelectRole,
} from "./formBuilderSelect";
import {
  formBuilderGenerateDefaultHeadingText,
  formBuilderGenerateDefaultParagraphText,
  IFormBuilderText,
  isFormBuilderTextRole,
  isFormBuilderTextType,
  TFormBuilderTextRole,
} from "./formBuilderText";
import {
  formBuilderGenerateDefaultCustomTextarea,
  formBuilderGenerateDefaultMessageTextarea,
  IFormBuilderTextarea,
  isFormBuilderTextareaRole,
  isFormBuilderTextareaType,
  TFormBuilderTextareaRole,
} from "./formBuilderTextarea";
import { IFormBuilderDesignStyles } from "../formBuilderDesignStyles";
import classNames from "classnames";

export interface IFormBuilderItemDataBase {
  formRowId: number;
  type: string;
  role: string;
  settings: unknown;
}

export interface IFormBuilderItemBase extends IFormBuilderEntityBase {
  type: "item";
  data: IFormBuilderItemDataBase;
}

type TFormBuilderField =
  | IFormBuilderCheckbox
  | IFormBuilderDate
  | IFormBuilderInput
  | IFormBuilderSelect
  | IFormBuilderTextarea;

export type TFormBuilderItem = IFormBuilderButton | IFormBuilderText | TFormBuilderField;

export type TFormBuilderItemData = TFormBuilderItem["data"];
export type TFormBuilderItemType = TFormBuilderItemData["type"];
export type TFormBuilderItemSettings = TFormBuilderItemData["settings"];
export type TFormBuilderItemRole = TFormBuilderItemData["role"];

type TPickFormBuilderItemKind<T> = T extends {
  type: TFormBuilderItemType;
  role: TFormBuilderItemRole;
}
  ? Pick<T, "type" | "role">
  : never;

export type TFormBuilderItemKind = TPickFormBuilderItemKind<TFormBuilderItemData>;
export type TFormBuilderItemKindKey = `${TFormBuilderItemType}-${TFormBuilderItemRole}`;

export function getFormBuilderItemKindKey(
  type: TFormBuilderItemType,
  role: TFormBuilderItemRole,
): TFormBuilderItemKindKey {
  return `${type}-${role}`;
}

export function isFormBuilderItemKind(type: string, role: string): boolean {
  return (
    (isFormBuilderButtonType(type) && isFormBuilderButtonRole(role)) ||
    (isFormBuilderCheckboxType(type) && isFormBuilderCheckboxRole(role)) ||
    (isFormBuilderDateType(type) && isFormBuilderDateRole(role)) ||
    (isFormBuilderInputType(type) && isFormBuilderInputRole(role)) ||
    (isFormBuilderSelectType(type) && isFormBuilderSelectRole(role)) ||
    (isFormBuilderTextType(type) && isFormBuilderTextRole(role)) ||
    (isFormBuilderTextareaType(type) && isFormBuilderTextareaRole(role))
  );
}

export function isWebsiteBuilderFormItemRawValid(rawItem: IWebsiteBuilderFormItemRaw): boolean {
  return (
    rawItem?.id != null &&
    rawItem?.form_row_id != null &&
    rawItem?.key != null &&
    isFormBuilderItemKind(rawItem.type, rawItem.role)
  );
}

function generateDefaultButton(role: TFormBuilderButtonRole): IFormBuilderButton {
  switch (role) {
    case "reset":
      return formBuilderGenerateDefaultResetButton();
    case "submit":
      return formBuilderGenerateDefaultSubmitButton();
    default: {
      // eslint-disable-next-line no-unused-vars
      const _exhaustiveCheck: never = role;
      return null;
    }
  }
}

function generateDefaultCheckbox(role: TFormBuilderCheckboxRole): IFormBuilderCheckbox {
  switch (role) {
    case "custom":
      return formBuilderGenerateDefaultCustomCheckbox();
    default: {
      // eslint-disable-next-line no-unused-vars
      const _exhaustiveCheck: never = role;
      return null;
    }
  }
}

function generateDefaultDate(role: TFormBuilderDateRole): IFormBuilderDate {
  switch (role) {
    case "custom":
      return formBuilderGenerateDefaultCustomDate();
    default: {
      // eslint-disable-next-line no-unused-vars
      const _exhaustiveCheck: never = role;
      return null;
    }
  }
}

function generateDefaultInput(role: TFormBuilderInputRole): IFormBuilderInput {
  switch (role) {
    case "address":
      return formBuilderGenerateDefaultAddressInput();
    case "custom":
      return formBuilderGenerateDefaultCustomInput();
    case "email":
      return formBuilderGenerateDefaultEmailInput();
    case "first-name":
      return formBuilderGenerateDefaultFirstNameInput();
    case "last-name":
      return formBuilderGenerateDefaultLastNameInput();
    case "phone":
      return formBuilderGenerateDefaultPhoneInput();
    default: {
      // eslint-disable-next-line no-unused-vars
      const _exhaustiveCheck: never = role;
      return null;
    }
  }
}

function generateDefaultSelect(role: TFormBuilderSelectRole): IFormBuilderSelect {
  switch (role) {
    case "custom":
      return formBuilderGenerateDefaultCustomSelect();
    case "inquiry":
      return formBuilderGenerateDefaultInquirySelect();
    default: {
      // eslint-disable-next-line no-unused-vars
      const _exhaustiveCheck: never = role;
      return null;
    }
  }
}

function generateDefaultText(role: TFormBuilderTextRole): IFormBuilderText {
  switch (role) {
    case "heading":
      return formBuilderGenerateDefaultHeadingText();
    case "paragraph":
      return formBuilderGenerateDefaultParagraphText();
    default: {
      // eslint-disable-next-line no-unused-vars
      const _exhaustiveCheck: never = role;
      return null;
    }
  }
}

function generateDefaultTextarea(role: TFormBuilderTextareaRole): IFormBuilderTextarea {
  switch (role) {
    case "custom":
      return formBuilderGenerateDefaultCustomTextarea();
    case "message":
      return formBuilderGenerateDefaultMessageTextarea();
    default: {
      // eslint-disable-next-line no-unused-vars
      const _exhaustiveCheck: never = role;
      return null;
    }
  }
}

export function formBuilderGenerateDefaultItem(kind: TFormBuilderItemKind): TFormBuilderItem {
  switch (kind.type) {
    case "button":
      return generateDefaultButton(kind.role);
    case "checkbox":
      return generateDefaultCheckbox(kind.role);
    case "date":
      return generateDefaultDate(kind.role);
    case "input":
      return generateDefaultInput(kind.role);
    case "select":
      return generateDefaultSelect(kind.role);
    case "text":
      return generateDefaultText(kind.role);
    case "textarea":
      return generateDefaultTextarea(kind.role);
    default: {
      // eslint-disable-next-line no-unused-vars
      const _exhaustiveCheck: never = kind;
      return null;
    }
  }
}

export function formBuilderGenerateItemDesignStyles(item: TFormBuilderItem): IFormBuilderDesignStyles {
  return {
    className: classNames("form-builder-item", {
      "item-fill": item.data.type !== "button",
      "item-hug": item.data.type === "button",
    }),
    styleProperties: {},
    styleDeclaration: "",
  };
}